// The page's side of the image worker. Starts one worker on first use and
// converts HEIC photos with heic2any when the browser can't open them itself.
import { wrap, type Remote } from "comlink";
import type { ImageJob, ImageOutput } from "@/lib/image/job";
import type { ImageWorkerApi } from "@/workers/image.worker";

let worker: Remote<ImageWorkerApi> | null = null;

function imageWorker() {
  worker ??= wrap<ImageWorkerApi>(
    new Worker(new URL("./image.worker.ts", import.meta.url), {
      type: "module",
    }),
  );
  return worker;
}

// HEIC files we already converted, so a preview and the final run share the work.
const heicCache = new WeakMap<Blob, Promise<Blob>>();

function heicToPng(file: Blob): Promise<Blob> {
  let png = heicCache.get(file);
  if (!png) {
    png = import("heic2any").then(async ({ default: heic2any }) => {
      const out = await heic2any({ blob: file, toType: "image/png" });
      return Array.isArray(out) ? out[0] : out;
    });
    heicCache.set(file, png);
  }
  return png;
}

async function withHeicFallback<T>(
  file: Blob,
  run: (input: Blob) => Promise<T>,
): Promise<T> {
  const cached = heicCache.get(file);
  if (cached) return run(await cached);
  try {
    return await run(file);
  } catch (error) {
    if ((error as Error)?.name !== "HeicUnsupportedError") throw error;
    try {
      return await run(await heicToPng(file));
    } catch {
      heicCache.delete(file);
      throw new Error("This HEIC photo couldn't be opened.");
    }
  }
}

export function runImageJob(file: Blob, job: ImageJob): Promise<ImageOutput> {
  return withHeicFallback(file, (input) => imageWorker().process(input, job));
}

export function measureImage(file: Blob) {
  return withHeicFallback(file, (input) => imageWorker().measure(input));
}
